import { customElement, scss } from '../../core/ids-decorators';
import { parseNumberWithUnits } from '../../utils/ids-dom-utils/ids-dom-utils';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { IdsDataGridFilterAttributes } from '../ids-data-grid/ids-data-grid-common';

import IdsButton from '../ids-button/ids-button';

import '../ids-icon/ids-icon';
import '../ids-popup-menu/ids-popup-menu';
import '../ids-menu/ids-menu-group';
import '../ids-menu/ids-menu-item';

import styles from '../ids-button/ids-button.scss';

import type IdsIcon from '../ids-icon/ids-icon';
import type IdsPopupMenu from '../ids-popup-menu/ids-popup-menu';

/**
 * IDS Menu Button Component
 * @type {IdsMenuButton}
 * @inherits IdsButton
 */
@customElement('ids-menu-button')
@scss(styles)
export default class IdsMenuButton extends IdsButton {
  constructor() {
    super();
  }

  /**
   * @returns {Array} containing configurable attributes on this component
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.ALIGN,
      attributes.DISABLED,
      attributes.DISPLAY_SELECTED_TEXT,
      attributes.DROPDOWN_ICON,
      attributes.FORMATTER_WIDTH,
      attributes.ID,
      attributes.MENU,
      attributes.VALUE
    ];
  }

  /**
   * Toggle-Button-level `connectedCallback` implementation (adds an icon refresh)
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute(attributes.DROPDOWN_ICON)) {
      this.#configureDropdownIcon(true);
      this.setPopupArrow();
    }
    // Defer the popup placement until after the initial setup phase
    requestAnimationFrame(() => {
      this.configureMenu();
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.container = null;
  }

  /**
   * @returns {string[]} containing CSS classes that will be added to the buttons
   */
  get protoClasses() {
    const textSlot = this.querySelector('span:not(.audible), ids-text:not(.audible)');
    const iconSlot = this.querySelector('ids-icon[slot]')
      || this.querySelector('ids-icon');
    if (iconSlot && (!textSlot)) {
      if (this.parentElement?.classList.contains('has-sub-tabs')) {
        return ['ids-menu-button', 'sub-tab-button', 'ids-icon-button'];
      }
      return ['ids-menu-button', 'ids-icon-button'];
    }
    return ['ids-menu-button'];
  }

  /**
   * @param {boolean | string} val true if the component should be disabledd
   */
  set disabled(val: boolean | string) {
    super.disabled = val;
    const truthyVal = stringToBool(val);
    if (this.menuEl) {
      this.menuEl.disabled = truthyVal;
    }
  }

  /**
   * @returns {boolean} true if the component is disabled
   */
  get disabled(): boolean | string {
    return super.disabled;
  }

  #configureDropdownIcon(doShow: boolean): void {
    const icon = this.dropdownIconEl;
    const iconType = (this.dropdownIcon?.length) ? this.dropdownIcon : 'dropdown';

    if (doShow) {
      if (!icon) {
        this.container?.insertAdjacentHTML('beforeend', `<ids-icon icon="${iconType}" class="ids-icon dropdown-icon"></ids-icon>`);
      } else {
        icon.icon = iconType;
      }
    } else if (icon) {
      icon.remove();
    }
  }

  /**
   * @param {boolean | string} val true if the menu button should adopt a selected menu item's text content when chosen
   */
  set displaySelectedText(val: boolean | string) {
    const bool = stringToBool(val);
    if (bool) this.setAttribute(attributes.DISPLAY_SELECTED_TEXT, `${val}`);
    else this.removeAttribute(attributes.DISPLAY_SELECTED_TEXT);
    this.configureMenu();
  }

  /**
   * @returns {boolean | string} true if the menu button will adopt a selected menu item's text content when chosen
   */
  get displaySelectedText() {
    return stringToBool(this.getAttribute(attributes.DISPLAY_SELECTED_TEXT));
  }

  /**
   * @param {string|undefined} val referencing an icon string name to use
   */
  set dropdownIcon(val) {
    const trueVal = stringToBool(val);
    if (trueVal) {
      this.setAttribute(attributes.DROPDOWN_ICON, (typeof val === 'string' && val.length) ? `${val}` : 'dropdown');
    } else {
      this.removeAttribute(attributes.DROPDOWN_ICON);
    }
    this.#configureDropdownIcon(trueVal);
    this.setPopupArrow();
  }

  /**
   * @returns {string|undefined} containing the type of icon being displayed as the Dropdown Icon
   */
  get dropdownIcon() {
    return this.getAttribute(attributes.DROPDOWN_ICON);
  }

  /**
   * @returns {HTMLElement|null} the decorative dropdown icon element
   */
  get dropdownIconEl() {
    return this.container?.querySelector<IdsIcon>('ids-icon:not([slot])');
  }

  /**
   * @returns {string|null} an ID selector string matching a menu
   */
  get menu() {
    return this.getAttribute(attributes.MENU);
  }

  /**
   * @param {string|null} val an ID selector string
   */
  set menu(val) {
    this.setAttribute(attributes.MENU, `${val}`);
    this.configureMenu();
  }

  /**
   * Retrieves a list of selected items from menu.
   * @returns {Array<any>} list of the values contained by selected menu items
   */
  get value() {
    const attrVal = this.getAttribute(attributes.VALUE);
    return this.menuEl?.getSelectedValues() ?? (attrVal ? [attrVal] : []);
  }

  /**
   * Set menu's selected items by value
   * @param {Array<any>|string} values array|string of value(s) contained in menu items
   * @returns {void}
   */
  set value(values) {
    if (typeof (this.menuEl?.setSelectedValues) === 'function') {
      this.menuEl.setSelectedValues(values);
    }
  }

  /**
   * Caches miss results of fallback menu lookups. Hits are always re-resolved.
   * @private
   */
  #fallbackLookupMissKey?: string;

  /**
   * Clears the cached fallback miss so the next `menuEl` access re-queries the DOM.
   * @returns {void}
   */
  resetMenuElCache(): void {
    this.#fallbackLookupMissKey = undefined;
  }

  /**
   * Walks ancestors to find the nearest menu matching `selector`. Bails early if no
   * match exists anywhere in the root.
   * @private
   * @param {Document | ShadowRoot} root The root node this button lives in
   * @param {string} selector The menu selector to match
   * @returns {HTMLElement | null} The closest matching menu, if any
   */
  #findClosestMenu(root: Document | ShadowRoot, selector: string): HTMLElement | null {
    try {
      const anyMatch = root?.querySelector<HTMLElement>(selector) ?? null;
      if (!anyMatch) return null;

      let scope = this.parentElement;
      while (scope) {
        const match = scope.querySelector<HTMLElement>(selector);
        if (match) return match;
        scope = scope.parentElement;
      }

      return anyMatch;
    } catch {
      return null;
    }
  }

  /**
   * @readonly
   * @returns {HTMLElement | null} element if one is present
   */
  get menuEl(): any {
    const menu = this.menu;
    const root = this.getRootNode() as Document | ShadowRoot;

    // Fast path — O(1) id lookup
    if (menu) {
      const byId = root?.getElementById?.(menu);
      const tagName = byId?.tagName;
      if (tagName === 'IDS-POPUP-MENU' || tagName === 'IDS-ACTION-SHEET') return byId as IdsPopupMenu;
    }

    // Slow path — selector scan with memoized misses to avoid repeated DOM walks
    const lookupKey = `${menu}|${this.id}`;
    if (this.#fallbackLookupMissKey === lookupKey) return null;

    let el: HTMLElement | null = null;

    if (menu) {
      // Handles hosts (e.g. ExtJS) that rewrite id attributes
      const escapedMenu = CSS.escape(menu);
      el = this.#findClosestMenu(root, `ids-popup-menu[class~="${escapedMenu}"],ids-popup-menu[data-menu-id="${menu}"],ids-action-sheet[id="${escapedMenu}"]`);
    }

    // Reverse lookup — menu targets this button by id
    if (!el && this.id) {
      el = this.#findClosestMenu(root, `ids-popup-menu[target="#${CSS.escape(this.id)}"]`);
    }

    this.#fallbackLookupMissKey = el ? undefined : lookupKey;

    return el;
  }

  /**
   * @returns {void}
   */
  configureMenu() {
    // Invalidate cache in case a menu was added since last access
    this.resetMenuElCache();

    const menuEl = this.menuEl;
    if (!menuEl?.popup) {
      return;
    }
    this.resizeMenu();
    menuEl.triggerType = 'click';
    menuEl.target = this;

    this.setAttribute(htmlAttributes.ARIA_HASPOPUP, 'menu');
    // Apply align from menu button's own attribute, or use default
    if (this.align) {
      menuEl.align = this.align;
    } else {
      menuEl.popup.align = 'bottom, left';
    }
    menuEl.popup.y = menuEl.target?.hasAttribute(IdsDataGridFilterAttributes.filterConditionButton) ? -2 : 4;

    this.setPopupArrow();

    // ====================================================================
    // Setup menu-specific event listeners, if they aren't already applied

    const hasBeforeShow = this?.handledEvents?.get('beforeshow');
    if (!hasBeforeShow) {
      // On the Popup Menu's `beforeshow` event, set the menu's size to the Menu Button's
      this.onEvent('beforeshow', menuEl, () => {
        this.setActiveState(true);
        this.resizeMenu();
      });
    }

    const hasHideHandler = this?.handledEvents?.get('hide');
    if (!hasHideHandler) {
      this.onEvent('hide', menuEl, () => {
        this.setActiveState(false);
      });
    }

    // Enabled using `displaySelectedText` setting
    this.offEvent('selected.menu');
    if (this.displaySelectedText) {
      this.onEvent('selected.menu', menuEl, (e: CustomEvent) => {
        this.updateBtnText(e.detail.elem.textContent.trim());
      });
    }
  }

  /**
   * Updates button text
   * @param {string} text button text
   */
  updateBtnText(text: string) {
    let btnTextEl = this.querySelector('ids-text, span');
    if (!btnTextEl) btnTextEl = this;
    btnTextEl.textContent = text;
  }

  /**
   * Set button's active state
   * @param {boolean} isActive true when menu is open
   */
  setActiveState(isActive: boolean) {
    if (isActive) {
      this.button?.classList.add('is-active');
    } else {
      this.button?.classList.remove('is-active');
    }
  }

  /**
   * @returns {void}
   */
  resizeMenu() {
    const popup = this.menuEl?.popup;
    if (!popup?.container) {
      return;
    }
    popup.container.style.minWidth = `${this.button?.clientWidth}px`;
  }

  /**
   * @returns {void}
   */
  setPopupArrow() {
    const menuEl = this.menuEl;
    if (!menuEl?.popup) {
      return;
    }

    const popupMenuEl = document.querySelector(`#${this.menu}`);
    let arrow = 'bottom';
    menuEl.popup.arrowTarget = this.dropdownIconEl || this;

    if (popupMenuEl && (popupMenuEl as IdsPopupMenu).getAttribute('arrow')) {
      arrow = (popupMenuEl as IdsPopupMenu).arrow || '';
    }
    menuEl.popup.arrow = arrow;
  }

  /**
   * Set the formatter width for menu button
   * @param {string | number} value The formatter width value
   */
  set formatterWidth(value: string | number) {
    const val = parseNumberWithUnits(value);

    this.container?.classList[/%$/g.test(val) ? 'add' : 'remove']('formatter-width-percentage');

    if (val) {
      this.setAttribute(attributes.FORMATTER_WIDTH, String(value));
      this.container?.classList.add(attributes.FORMATTER_WIDTH);
      if (this.container) this.container.style.minWidth = val;
    } else {
      this.removeAttribute(attributes.FORMATTER_WIDTH);
      this.container?.classList.remove(attributes.FORMATTER_WIDTH);
      if (this.container) this.container.style.minWidth = '';
    }
  }

  /**
   * Set the align attribute for the popup menu
   * @param {string} value The align value
   */
  set align(value: string) {
    if (value) {
      this.setAttribute(attributes.ALIGN, value);
      if (this.menuEl) {
        this.menuEl.align = value;
      }
    } else {
      this.removeAttribute(attributes.ALIGN);
    }
  }

  /**
   * Get the align attribute
   * @returns {string} The align value
   */
  get align(): string {
    return this.getAttribute(attributes.ALIGN) ?? '';
  }
}
